import { ModalAddAddress } from "features/Agent/components";
import { ORDER_PRIORITY, ORDER_TYPE } from "features/Cart/constants";
import { getCartOrderValue, getShippingTypeOptions } from "features/Cart/shippingPolicy";
import { useGetShippingTypes } from "hooks/cart";
import { useGetContactsAll } from "hooks/contact";
import useDebounce from "hooks/useDebounce";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { getQuery, notify } from "utils/helperFuncs";

const useInfoAgent = ({ data, handleUpdate, refetchCart, shippingTypes: shippingTypesFromParent, dataOrder }) => {
  const GET_QUERY = getQuery();
  const { sender, sellerID, allSenders, shippingContact } = data || {};
  const [searchTerm, setSearchTerm] = useState("");
  const debounceValue = useDebounce(searchTerm);

  const [paramsContactsAll, setParamsContactsAll] = useState({
    filters: {
      query: GET_QUERY.query || undefined,
      sellerID: sellerID || null,
    },
    pagination: {
      offset: GET_QUERY?.offset || 0,
      limit: 10,
    },
  });

  // `data` (dataInfoAgent) luôn là object đủ key dù cart chưa load (`convertGetCart(undefined)`
  // không trả `{}`/`undefined`) nên `!data` luôn `false` — dùng `dataOrder` (cart RAW, thực sự
  // `undefined` tới khi `GetCart` xong) để skip mới đúng, tránh 1 request "rác" lúc mount.
  const {
    data: allContacts,
    loading: loadingContactList,
    paginationData,
    refetch,
  } = useGetContactsAll(paramsContactsAll, { sellerID: sellerID, skip: !dataOrder });

  function onTableChange(pagination) {
    const { current, pageSize } = pagination;
    let newParams = {
      ...paramsContactsAll,
      pagination: { ...paramsContactsAll?.pagination, offset: (current - 1) * pageSize },
    };

    setParamsContactsAll(newParams);
  }

  const handleChangeInput = (e) => {
    setSearchTerm(e);
  };

  const orderTypeOptions = [
    {
      value: ORDER_TYPE.SELLER,
      label: t("cart.orderType.seller"),
    },
    {
      value: ORDER_TYPE.CUSTOMER,
      label: t("cart.orderType.customer"),
    },
  ];

  const onSubmitSenderAddress = (selected) => {
    const newSender = allSenders?.find((item) => item.id === selected);
    const senderUpload = {
      senderName: newSender?.fullName,
      senderPhone: newSender?.telephone,
      senderAddress: newSender?.address,
    };
    handleUpdate({ ...senderUpload, shippingConfig: data?.shippingConfiguration }).then(() => refetchCart());
  };

  const onSubmitReceiverAddress = (selected) => {
    const newReceiver = allContacts?.find((item) => item.id === selected);
    handleUpdate({ shippingContactID: newReceiver?.id, shippingConfig: data?.shippingConfiguration }).then(() =>
      refetchCart()
    );
  };

  const infoReceiverAndSender = [
    {
      title: t("cart.infoReceiverAndSender.sender"),
      id: sender?.id,
      name: sender?.fullName,
      phoneNumber: sender?.telephone,
      address: sender?.address,
      titleModal: t("cart.senderAddress"),
      list: allSenders,
      onSubmit: onSubmitSenderAddress,
      addMoreButton: null,
      senderAddressVersion: sender?.version,
    },
    {
      title: t("cart.infoReceiverAndSender.receiver"),
      id: shippingContact?.id,
      name: shippingContact?.fullName,
      phoneNumber: shippingContact?.telephone,
      address: shippingContact?.address,
      titleModal: t("cart.receiverAddress"),
      list: allContacts,
      onSubmit: onSubmitReceiverAddress,
      addMoreButton: <ModalAddAddress refetch={refetch} sellerID={sellerID} />,
      paginationData: paginationData,
      params: paramsContactsAll,
      loadingContactList: loadingContactList,
      onTableChange: onTableChange,
      isReceiver: true,
      handleChangeInput: handleChangeInput,
      shippingAddressVersion: shippingContact?.wardVersion,
    },
  ];

  // `enable_new_shipping_provider` chỉ quyết định nguồn dữ liệu ĐVVC (AdditionalDeliver/useList),
  // không liên quan tới danh sách PTVC + trạng thái disable dưới đây.

  // `useList` (cha, xem List/useList.js) đã gọi `useGetShippingTypes` với CÙNG variables để phục vụ
  // logic tự fix PTVC không hợp lệ — gọi lại ở đây là trùng request. Chỉ tự fetch khi cha chưa có
  // (vd. `InfoAgent` được dùng nơi khác không truyền `shippingTypes` xuống).
  const { data: shippingTypesFetched } = useGetShippingTypes({
    contactCityID: shippingContact?.cityID,
    contactWardID: shippingContact?.wardID,
    exportedWarehouseID: data?.exportedWarehouse?.id,
    subTotal: getCartOrderValue(data),
    skip: Boolean(shippingTypesFromParent),
  });
  const shippingTypes = shippingTypesFromParent ?? shippingTypesFetched;

  const shippingTypeOptions = getShippingTypeOptions(shippingTypes);

  const rawShippingType = data?.shippingType;

  // PTVC đang chọn không còn hợp lệ (rớt xuống dưới ngưỡng, hoặc BE báo hết hỗ trợ theo zone/tuyến)
  // — không render khối phụ theo giá trị cũ nữa, chờ Sale chọn lại.
  const isShippingTypeInvalid = Boolean(
    shippingTypeOptions?.find((option) => option?.value === rawShippingType)?.disabled
  );

  const effectiveShippingType = isShippingTypeInvalid ? undefined : rawShippingType;

  const orderPriorityOptions = [
    {
      value: ORDER_PRIORITY.LOW,
      label: t("cart.orderPriority.low"),
    },
    {
      value: ORDER_PRIORITY.MEDIUM,
      label: t("cart.orderPriority.medium"),
    },

    {
      value: ORDER_PRIORITY.HIGH,
      label: t("cart.orderPriority.high"),
    },
  ];

  const onChange = async (value) => {
    try {
      await handleUpdate(value).then(() => refetchCart());
    } catch (error) {
      notify.error({ message: error.message });
    }
  };

  const onChangeDate = (date, dateString) => {
    const formatDate = date?.toISOString(true);
    handleUpdate({ deliveryDeadline: formatDate }).then(() => refetchCart());
  };

  useEffect(() => {
    setParamsContactsAll((prev) => ({
      ...prev,
      filters: { ...prev.filters, query: debounceValue },
      pagination: { offset: 0, limit: 10 },
    }));
  }, [debounceValue]);

  return {
    orderTypeOptions,
    infoReceiverAndSender,
    shippingTypeOptions,
    orderPriorityOptions,
    onChange,
    onChangeDate,
    handleChangeInput,
    effectiveShippingType,
    isShippingTypeInvalid,
  };
};

export default useInfoAgent;
